import { PencilIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ROUTES from '@/ROUTES'
import { format } from 'date-fns'

import { IUser } from '@/MODELS/user.model'

import { api, api_delete } from '@/services/api.service'
import { useResource } from '@/services/resource.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Avatar from '@/components/Avatar'
import Button from '@/components/Button'
import Table from '@/components/Table'

interface DeletionPreview {
    user: { name: string; email: string; createdAt: string; onboardingComplete: boolean }
    dataCounts: { roadmapSteps: number; chats: number; chatSessions: number; events: number; posts: number; comments: number }
    subscriptions: {
        stripeSubscriptionId: string
        subscriptionStatus: string
        planType?: string
        trialEndDate?: string
        benefitsEndDate?: string
        createdAt: string
    }[]
}

type DeleteStep = 'closed' | 'loading' | 'preview' | 'cancel-subscription' | 'confirm-delete'

export default function AdminUserTable() {
    const navigate = useNavigate()
    const [data, setData, UserResource] = useResource<IUser[]>(ROUTES.admin.user)
    const [deleteUserId, setDeleteUserId] = useState<string | null>(null)
    const [deleteStep, setDeleteStep] = useState<DeleteStep>('closed')
    const [preview, setPreview] = useState<DeletionPreview | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isProcessing, setIsProcessing] = useState(false)

    async function startDeleteFlow(userId: string) {
        setDeleteUserId(userId)
        setDeleteStep('loading')
        setError(null)

        try {
            const response = await api<DeletionPreview>(`${ROUTES.admin.user}/${userId}/deletion-preview`)
            if (response.status === 200 && response.data) {
                setPreview(response.data)
                setDeleteStep('preview')
            } else {
                setError('Failed to load user data')
                setDeleteStep('closed')
            }
        } catch {
            setError('Failed to load user data')
            setDeleteStep('closed')
        }
    }

    function closeDeleteFlow() {
        setDeleteUserId(null)
        setDeleteStep('closed')
        setPreview(null)
        setError(null)
        setIsProcessing(false)
    }

    const hasActiveSubscriptions = preview?.subscriptions?.some((s) =>
        ['active', 'trialing', 'past_due'].includes(s.subscriptionStatus)
    )

    async function handleCancelSubscriptions() {
        if (!deleteUserId) return
        setIsProcessing(true)
        setError(null)

        try {
            const response = await api(`${ROUTES.admin.user}/${deleteUserId}/cancel-subscription`, {})
            if (response.status === 200) {
                // Refresh the preview to show updated subscription status
                const refreshed = await api<DeletionPreview>(`${ROUTES.admin.user}/${deleteUserId}/deletion-preview`)
                if (refreshed.status === 200 && refreshed.data) {
                    setPreview(refreshed.data)
                }
                setDeleteStep('confirm-delete')
            } else {
                setError('Failed to cancel subscriptions')
            }
        } catch {
            setError('Failed to cancel subscriptions')
        } finally {
            setIsProcessing(false)
        }
    }

    async function handleDeleteUser() {
        if (!deleteUserId) return
        setIsProcessing(true)
        setError(null)

        try {
            const response = await api_delete(`${ROUTES.admin.user}/${deleteUserId}`)
            if (response.status && response.status >= 200 && response.status < 300) {
                await UserResource.get()
                closeDeleteFlow()
            } else {
                // Surface the server's actionable message (e.g. "remove them from
                // the organization first") instead of a generic failure, so the
                // admin knows exactly what to do before they can delete the user.
                setError(response.error ?? 'Failed to delete user')
                setIsProcessing(false)
            }
        } catch (err: any) {
            setError(err?.error ?? 'Failed to delete user')
            setIsProcessing(false)
        }
    }

    function formatStatus(status: string) {
        const colors: Record<string, string> = {
            active: 'bg-green-100 text-green-800',
            trialing: 'bg-blue-100 text-blue-800',
            past_due: 'bg-yellow-100 text-yellow-800',
            canceled: 'bg-gray-100 text-gray-800',
            unpaid: 'bg-red-100 text-red-800',
            paused: 'bg-gray-100 text-gray-600',
        }
        return (
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
                {status}
            </span>
        )
    }

    return (
        <div className='flex flex-col w-full max-w-7xl gap-5 py-10 mx-auto'>
            <AdminPageHeader
                heading={'User Management'}
                subheading='Manage user access and profile details'
                className='py-5'
            />
            <Table
                data={data ?? []}
                defaultSortColumn='Created At'
                defaultSortDirection='desc'
                columns={[
                    {
                        header: '',
                        cell: (user) => <Avatar user={user} size='sm' />,
                    },
                    {
                        header: 'Name',
                        cell: (user) => `${user.name} `,
                    },
                    {
                        header: 'Email',
                        cell: 'email',
                    },
                    {
                        header: 'Role',
                        cell: (user) => {
                            const roles: string[] = []
                            if (user.permissions.isAdmin) roles.push('Admin')
                            if ((user as any).testUser?.enabled) roles.push('Test User')
                            if (roles.length === 0) roles.push('User')
                            return roles.join(', ')
                        },
                        filter: [
                            { label: 'Admin', fn: (user) => user.permissions.isAdmin },
                            { label: 'User', fn: (user) => !user.permissions.isAdmin && !(user as any).testUser?.enabled },
                            { label: 'Test User', fn: (user) => (user as any).testUser?.enabled },
                        ],
                    },
                    {
                        header: 'Created At',
                        cell: (user) => format(new Date(user.createdAt), 'PPPp'),
                        sort: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
                    },
                    {
                        header: 'Last Login',
                        cell: (user) => (user.lastLoginAt ? format(new Date(user.lastLoginAt), 'PPPp') : 'Never'),
                    },
                    {
                        header: '',
                        cell: (user) => (
                            <Link
                                to={`/admin/user/${user._id}`}
                                className='transition-opacity hover:opacity-70'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <PencilIcon className='w-6 h-6 shrink-0' />
                            </Link>
                        ),
                    },
                    {
                        header: '',
                        cell: (user) => (
                            <Button.Trash
                                onClick={(e) => {
                                    e.stopPropagation()
                                    startDeleteFlow(user._id)
                                }}
                            />
                        ),
                    },
                ]}
                onRowClick={(user) => navigate(`/admin/user/${user._id}`)}
            />

            {/* Multi-step deletion modal */}
            {deleteStep !== 'closed' && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
                    <div className='w-full max-w-lg p-6 mx-4 bg-white rounded-xl shadow-2xl'>

                        {/* Loading state */}
                        {deleteStep === 'loading' && (
                            <div className='flex items-center justify-center py-12'>
                                <div className='w-8 h-8 border-4 border-gray-200 rounded-full border-t-brand-600 animate-spin' />
                                <span className='ml-3 text-gray-600'>Loading user data...</span>
                            </div>
                        )}

                        {/* Step 1: Preview — show what will be affected */}
                        {deleteStep === 'preview' && preview && (
                            <>
                                <div className='flex items-start gap-3 mb-4'>
                                    <div className='flex items-center justify-center flex-shrink-0 w-10 h-10 bg-red-100 rounded-full'>
                                        <svg className='w-5 h-5 text-red-600' fill='none' viewBox='0 0 24 24' strokeWidth='2' stroke='currentColor'>
                                            <path strokeLinecap='round' strokeLinejoin='round' d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z' />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className='text-lg font-semibold text-gray-900'>Delete User</h3>
                                        <p className='text-sm text-gray-500'>Review what will be permanently deleted</p>
                                    </div>
                                </div>

                                {/* User info */}
                                <div className='p-3 mb-4 rounded-lg bg-gray-50 border border-gray-200'>
                                    <p className='font-medium text-gray-900'>{preview.user.name || 'No name'}</p>
                                    <p className='text-sm text-gray-600'>{preview.user.email}</p>
                                    <p className='text-xs text-gray-400 mt-1'>
                                        Registered {format(new Date(preview.user.createdAt), 'PPP')}
                                        {preview.user.onboardingComplete ? ' · Onboarding complete' : ' · Onboarding not completed'}
                                    </p>
                                </div>

                                {/* Subscriptions (highlighted) */}
                                {preview.subscriptions.length > 0 && (
                                    <div className={`p-3 mb-4 rounded-lg border ${hasActiveSubscriptions ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200'}`}>
                                        <p className='text-sm font-semibold text-gray-900 mb-2'>
                                            {hasActiveSubscriptions ? 'Active Subscription' : 'Subscriptions'}
                                        </p>
                                        {preview.subscriptions.map((sub) => (
                                            <div key={sub.stripeSubscriptionId} className='flex items-center justify-between text-sm'>
                                                <span className='text-gray-700 font-mono text-xs'>{sub.stripeSubscriptionId}</span>
                                                {formatStatus(sub.subscriptionStatus)}
                                            </div>
                                        ))}
                                        {hasActiveSubscriptions && (
                                            <p className='mt-2 text-xs text-yellow-800 font-medium'>
                                                You must cancel the subscription in Stripe before deleting this user.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Data counts */}
                                <div className='p-3 mb-4 rounded-lg bg-gray-50 border border-gray-200'>
                                    <p className='text-sm font-semibold text-gray-900 mb-2'>Data to be deleted</p>
                                    <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600'>
                                        <span>Roadmap steps: <strong>{preview.dataCounts.roadmapSteps}</strong></span>
                                        <span>Chat conversations: <strong>{preview.dataCounts.chats}</strong></span>
                                        <span>Chat sessions: <strong>{preview.dataCounts.chatSessions}</strong></span>
                                        <span>Events: <strong>{preview.dataCounts.events}</strong></span>
                                        <span>Posts: <strong>{preview.dataCounts.posts}</strong></span>
                                        <span>Comments: <strong>{preview.dataCounts.comments}</strong></span>
                                    </div>
                                </div>

                                {error && <p className='mb-3 text-sm text-red-600'>{error}</p>}

                                <div className='flex gap-3'>
                                    <button
                                        onClick={closeDeleteFlow}
                                        className='flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50'
                                    >
                                        Cancel
                                    </button>
                                    {hasActiveSubscriptions ? (
                                        <button
                                            onClick={() => setDeleteStep('cancel-subscription')}
                                            className='flex-1 px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700'
                                        >
                                            Cancel Subscription First
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setDeleteStep('confirm-delete')}
                                            className='flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700'
                                        >
                                            Proceed to Delete
                                        </button>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Step 2: Cancel subscription confirmation */}
                        {deleteStep === 'cancel-subscription' && preview && (
                            <>
                                <div className='flex items-start gap-3 mb-4'>
                                    <div className='flex items-center justify-center flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full'>
                                        <svg className='w-5 h-5 text-yellow-600' fill='none' viewBox='0 0 24 24' strokeWidth='2' stroke='currentColor'>
                                            <path strokeLinecap='round' strokeLinejoin='round' d='M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z' />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className='text-lg font-semibold text-gray-900'>Cancel Stripe Subscription</h3>
                                        <p className='text-sm text-gray-500'>This will cancel billing in Stripe immediately</p>
                                    </div>
                                </div>

                                <div className='p-3 mb-4 rounded-lg bg-yellow-50 border border-yellow-300'>
                                    <p className='text-sm text-yellow-900'>
                                        <strong>{preview.user.email}</strong> has an active Stripe subscription.
                                        Cancelling will immediately stop billing. This cannot be undone.
                                    </p>
                                    {preview.subscriptions
                                        .filter((s) => ['active', 'trialing', 'past_due'].includes(s.subscriptionStatus))
                                        .map((sub) => (
                                            <div key={sub.stripeSubscriptionId} className='mt-2 flex items-center justify-between text-sm'>
                                                <span className='text-yellow-800 font-mono text-xs'>{sub.stripeSubscriptionId}</span>
                                                {formatStatus(sub.subscriptionStatus)}
                                            </div>
                                        ))}
                                </div>

                                {error && <p className='mb-3 text-sm text-red-600'>{error}</p>}

                                <div className='flex gap-3'>
                                    <button
                                        onClick={() => setDeleteStep('preview')}
                                        disabled={isProcessing}
                                        className='flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50'
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={handleCancelSubscriptions}
                                        disabled={isProcessing}
                                        className='flex-1 px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50'
                                    >
                                        {isProcessing ? 'Cancelling...' : 'Yes, Cancel Subscription'}
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Step 3: Final delete confirmation */}
                        {deleteStep === 'confirm-delete' && preview && (
                            <>
                                <div className='flex items-start gap-3 mb-4'>
                                    <div className='flex items-center justify-center flex-shrink-0 w-10 h-10 bg-red-100 rounded-full'>
                                        <svg className='w-5 h-5 text-red-600' fill='none' viewBox='0 0 24 24' strokeWidth='2' stroke='currentColor'>
                                            <path strokeLinecap='round' strokeLinejoin='round' d='m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0' />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className='text-lg font-semibold text-gray-900'>Permanently Delete User</h3>
                                        <p className='text-sm text-gray-500'>This action is irreversible</p>
                                    </div>
                                </div>

                                <div className='p-3 mb-4 rounded-lg bg-red-50 border border-red-200'>
                                    <p className='text-sm text-red-900'>
                                        You are about to permanently delete <strong>{preview.user.email}</strong> and all associated data.
                                        This includes their roadmap, chats, posts, and all other records. This cannot be undone.
                                    </p>
                                </div>

                                {error && <p className='mb-3 text-sm text-red-600'>{error}</p>}

                                <div className='flex gap-3'>
                                    <button
                                        onClick={closeDeleteFlow}
                                        disabled={isProcessing}
                                        className='flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50'
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDeleteUser}
                                        disabled={isProcessing}
                                        className='flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50'
                                    >
                                        {isProcessing ? 'Deleting...' : 'Yes, Permanently Delete'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
